import styled from 'styled-components';
import { ScrollingColumn } from 'foundations-components/transitional/components/ScrollingColumn';
import { ColumnHeaderV2, COLUMN_HEADER_HEIGHT } from './ColumnHeaderV2';
import { ColumnDndProvider, useColumnDndContext } from 'customer-data-board/ColumnDndProvider';
import ColumnContent from '../ColumnContent';
import { CollapsedColumnContentV2 } from './CollapsedColumnContentV2';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useColumnColors } from './useColumnColors';
import { useConfigs } from '../lib/config-context';
import { ColumnFooterV2 } from './ColumnFooterV2';
import { LoadMoreV2 } from './LoadMoreV2';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const COLUMN_WIDTH = '282px';
const COLLAPSED_COLUMN_WIDTH = `${COLUMN_HEADER_HEIGHT}px`;
const StyledColumnContent = styled.div.withConfig({
  displayName: "ColumnV2__StyledColumnContent",
  componentId: "sc-1595puq-0"
})(({
  theme,
  columnBodyColor,
  collapsed
}) => `
  background-color: ${theme.color[columnBodyColor]};
  height: 100%;
  overflow-y: auto;
  padding-inline-start: ${theme.space['250']};
  padding-inline-end: ${theme.space['250']};
  padding-top: ${theme.space['250']};
  padding-bottom: ${theme.space['250']};
${collapsed ? `
  border-right: 1px solid ${theme.color['border-core-subtle']};
  border-left: 1px solid ${theme.color['border-core-subtle']};
  border-bottom: 1px solid ${theme.color['border-core-subtle']};
  ` : ''}
`);
const StyledColumnContainer = styled.div.withConfig({
  displayName: "ColumnV2__StyledColumnContainer",
  componentId: "sc-1595puq-1"
})(({
  theme
}) => `
  margin-inline-end: ${theme.space['300']};

  &:last-child {
    margin-inline-end: ${theme.space['0']};
  }
`);
const ColumnV2Inner = ({
  columnData,
  shouldRenderCards,
  isBoardEmpty
}) => {
  const {
    cardRef,
    cardDroppableProps,
    cardPlaceholder
  } = useColumnDndContext();
  const {
    onColumnCollapse
  } = useConfigs();
  const [hovered, setHovered] = useState(false);
  const handleHover = useCallback(__hovered => {
    setHovered(__hovered);
  }, []);
  const shouldCollapse = useMemo(() => {
    if (isBoardEmpty) {
      return false;
    }
    return Boolean(columnData.collapsed);
  }, [isBoardEmpty, columnData.collapsed]);
  const {
    columnBackgroundColor,
    columnBodyColor
  } = useColumnColors(hovered, shouldCollapse);
  return /*#__PURE__*/_jsx(StyledColumnContainer, {
    children: /*#__PURE__*/_jsx(ScrollingColumn, {
      inlineSize: shouldCollapse ? COLLAPSED_COLUMN_WIDTH : COLUMN_WIDTH,
      headerContent: /*#__PURE__*/_jsx(ColumnHeaderV2, {
        isBoardEmpty: isBoardEmpty,
        onHovered: handleHover,
        columnData: columnData,
        columnBackgroundColor: columnBackgroundColor,
        onExpandCollapse: onColumnCollapse
      }),
      footerContent: /*#__PURE__*/_jsx(ColumnFooterV2, {
        columnData: columnData
      }),
      "data-test-id": "cdb-column",
      children: /*#__PURE__*/_jsx(StyledColumnContent, Object.assign({
        ref: cardRef,
        columnBodyColor: columnBodyColor,
        collapsed: shouldCollapse,
        "data-test-id": "cdb-column-content"
      }, cardDroppableProps, {
        children: shouldCollapse ? /*#__PURE__*/_jsx(CollapsedColumnContentV2, {
          columnData: columnData
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ColumnContent, {
            columnData: columnData,
            shouldRenderCards: shouldRenderCards
          }), cardPlaceholder, /*#__PURE__*/_jsx(LoadMoreV2, {
            columnData: columnData,
            shouldRenderCards: shouldRenderCards
          })]
        })
      }))
    })
  });
};
export const ColumnV2 = ({
  shouldRenderCards,
  columnData,
  isBoardEmpty
}) => {
  const {
    onColumnMounted
  } = useConfigs();
  const shouldCollapse = useMemo(() => {
    if (isBoardEmpty) {
      return false;
    }
    return Boolean(columnData.collapsed);
  }, [isBoardEmpty, columnData.collapsed]);
  const {
    columnBackgroundColor
  } = useColumnColors(false, shouldCollapse);
  useEffect(() => {
    if (onColumnMounted) {
      onColumnMounted(columnData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (isBoardEmpty) {
    return /*#__PURE__*/_jsx(StyledColumnContainer, {
      children: /*#__PURE__*/_jsx(ScrollingColumn, {
        headerContent: /*#__PURE__*/_jsx(ColumnHeaderV2, {
          isBoardEmpty: isBoardEmpty,
          columnData: columnData,
          columnBackgroundColor: columnBackgroundColor,
          onExpandCollapse: undefined // We don't want to allow expanding/collapsing a column in an empty board
        })
      })
    });
  }
  return /*#__PURE__*/_jsx(ColumnDndProvider, {
    columnData: columnData,
    children: /*#__PURE__*/_jsx(ColumnV2Inner, {
      columnData: columnData,
      shouldRenderCards: shouldRenderCards,
      isBoardEmpty: isBoardEmpty
    })
  });
};